import {
    Autocomplete,
    Button,
    Card,
    CardHeader,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Switch,
    TextField
} from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import { useEffect, useMemo, useState } from 'react';
import ChipSelect from '../../tools/ChipSelect';

function not(a, b) {
    return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a, b) {
    return a.filter((value) => b.indexOf(value) !== -1);
}

function union(a, b) {
    return [...a, ...not(b, a)];
}

const BulkUserAchievementAssignmentModal = ({
    open,
    onClose,
    users,
    allAchievements,
    achievementsProgress,
    postAchievementsProgressBulk,
    isPostingBulk,
    teams,
}) => {
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedTeams, setSelectedTeams] = useState([]);
    const [checked, setChecked] = useState([]);
    const [left, setLeft] = useState([]);
    const [right, setRight] = useState([]);
    const [leftSearch, setLeftSearch] = useState('');
    const [rightSearch, setRightSearch] = useState('');
    const [isCompleted, setIsCompleted] = useState(true);

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    const numberOfChecked = (items) => intersection(checked, items).length;

    const handleToggleAll = (items) => () => {
        if (numberOfChecked(items) === items.length) {
            setChecked(not(checked, items));
        } else {
            setChecked(union(checked, items));
        }
    };

    useEffect(() => {
        if (selectedUser) {
            const userAchievements = achievementsProgress
                .filter(p => p.user_id === selectedUser.value)
                .map(p => p.achievement_id);

            const achievementsTheUserHas = allAchievements.filter(a => userAchievements.includes(a.achievement_id));
            const achievementsTheUserDoesnt = allAchievements.filter(a => !userAchievements.includes(a.achievement_id));

            setRight(achievementsTheUserHas);
            setLeft(achievementsTheUserDoesnt);
        } else {
            setLeft(allAchievements);
            setRight([]);
        }
    }, [selectedUser, allAchievements, achievementsProgress]);

    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const handleAllRight = () => {
        setRight(right.concat(left));
        setLeft([]);
    };

    const handleCheckedRight = () => {
        setRight(right.concat(leftChecked));
        setLeft(not(left, leftChecked));
        setChecked(not(checked, leftChecked));
    };

    const handleCheckedLeft = () => {
        setLeft(left.concat(rightChecked));
        setRight(not(right, rightChecked));
        setChecked(not(checked, rightChecked));
    };

    const handleAllLeft = () => {
        setLeft(left.concat(right));
        setRight([]);
    };

    const handleSave = () => {
        const payload = {
            user_id: selectedUser.value,
            achievement_ids: right.map(a => a.achievement_id),
            is_completed: isCompleted,
        };
        postAchievementsProgressBulk(payload);
    };

    const filteredLeft = left.filter(a => a.name.toLowerCase().includes(leftSearch.toLowerCase()));
    const filteredRight = right.filter(a => a.name.toLowerCase().includes(rightSearch.toLowerCase()));

    const usersInTeam = useMemo(() => {
        if (selectedTeams.length === 0) return users;
        const userIdsInSelectedTeams = new Set();
        selectedTeams.forEach(teamId => {
            if (teamId === 'other') {
                const allUserIdsInTeams = new Set(teams.flatMap(team => team.members.map(m => m.user_id)));
                users.forEach(u => {
                    if (!allUserIdsInTeams.has(u.value)) {
                        userIdsInSelectedTeams.add(u.value);
                    }
                });
            } else {
                const team = teams.find(t => t.team_id === teamId);
                if (team) {
                    team.members.forEach(m => userIdsInSelectedTeams.add(m.user_id));
                }
            }
        });
        return users.filter(u => userIdsInSelectedTeams.has(u.value));
    }, [selectedTeams, teams, users]);

    const customList = (title, items, searchTerm, setSearchTerm) => (
        <Card>
            <CardHeader
                sx={{ px: 2, py: 1 }}
                avatar={
                    <Checkbox
                        onClick={handleToggleAll(items)}
                        checked={numberOfChecked(items) === items.length && items.length !== 0}
                        indeterminate={
                            numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0
                        }
                        disabled={items.length === 0}
                        inputProps={{
                            'aria-label': 'all items selected',
                        }}
                    />
                }
                title={title}
                subheader={`${items.length} achievements`}
            />
            <TextField
                sx={{ m: 1, width: 'calc(100% - 16px)' }}
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Divider />
            <List
                sx={{
                    width: 300,
                    height: 230,
                    bgcolor: 'background.paper',
                    overflow: 'auto',
                }}
                dense
                component="div"
                role="list"
            >
                {items.map((achievement) => {
                    const labelId = `transfer-list-item-${achievement.achievement_id}-label`;
                    return (
                        <ListItem
                            key={achievement.achievement_id}
                            role="listitem"
                            button
                            onClick={handleToggle(achievement)}
                        >
                            <ListItemIcon>
                                <Checkbox
                                    checked={checked.indexOf(achievement) !== -1}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{
                                        'aria-labelledby': labelId,
                                    }}
                                />
                            </ListItemIcon>
                            <ListItemText id={labelId} primary={achievement.name} />
                        </ListItem>
                    );
                })}
                <ListItem />
            </List>
        </Card>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Bulk Assign Achievements to User</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                        <ChipSelect
                            label="Teams"
                            options={teams ? [...teams.map(t => ({ label: t.team_name, value: t.team_id })), { label: 'Other', value: 'other' }] : []}
                            selectedValues={selectedTeams}
                            onChange={setSelectedTeams}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Autocomplete
                            options={usersInTeam}
                            getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                            value={selectedUser}
                            onChange={(event, newValue) => setSelectedUser(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select User"
                                    required
                                    size="small"
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isCompleted}
                                    onChange={(e) => setIsCompleted(e.target.checked)}
                                />
                            }
                            label="Mark as completed"
                        />
                    </Grid>
                    <Grid container spacing={2} justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
                        <Grid item>{customList('Available Achievements', filteredLeft, leftSearch, setLeftSearch)}</Grid>
                        <Grid item>
                            <Grid container direction="column" alignItems="center">
                                <Button
                                    sx={{ my: 0.5 }}
                                    variant="outlined"
                                    size="small"
                                    onClick={handleAllRight}
                                    disabled={left.length === 0}
                                    aria-label="move all right"
                                >
                                    ≫
                                </Button>
                                <Button
                                    sx={{ my: 0.5 }}
                                    variant="outlined"
                                    size="small"
                                    onClick={handleCheckedRight}
                                    disabled={leftChecked.length === 0}
                                    aria-label="move selected right"
                                >
                                    <IconComponent iconName="chevron-forward" />
                                </Button>
                                <Button
                                    sx={{ my: 0.5 }}
                                    variant="outlined"
                                    size="small"
                                    onClick={handleCheckedLeft}
                                    disabled={rightChecked.length === 0}
                                    aria-label="move selected left"
                                >
                                    <IconComponent iconName="chevron-back" />
                                </Button>
                                <Button
                                    sx={{ my: 0.5 }}
                                    variant="outlined"
                                    size="small"
                                    onClick={handleAllLeft}
                                    disabled={right.length === 0}
                                    aria-label="move all left"
                                >
                                    ≪
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid item>{customList('Assigned Achievements', filteredRight, rightSearch, setRightSearch)}</Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isPostingBulk}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!selectedUser || isPostingBulk}
                    startIcon={isPostingBulk ? <IconComponent iconName="spinner" /> : null}
                >
                    {isPostingBulk ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkUserAchievementAssignmentModal;
